import React, { useState, useEffect, useMemo, useRef, useCallback } from "react"; // CHANGED: Added useRef
import "./App.css";
import { jwtDecode } from "jwt-decode";
import MainApp from "./components/mainApp";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BackgroundStreamProvider } from "./contexts/BackgroundStreamContext";
import SuperAdminProvider from "./context/SuperAdminContext";
import { DarkModeProvider, useDarkMode } from "./context/DarkModeContext";
import { ProfilePictureProvider } from "./context/ProfilePictureContext";
import { BrowserRouter, useNavigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Capacitor } from "@capacitor/core";
import { SocialLogin } from "@capgo/capacitor-social-login";
import { endSessionAndRedirect, LOGIN_REASONS } from "./utils/sessionAuth";
import { loadUserFromStorage } from "./utils/authStorage";
import { useSessionRefresh } from "./hooks/useSessionRefresh";
import RouteUsageTracker from "./components/RouteUsageTracker";
import { usePullToRefresh } from "./hooks/usePullToRefresh";
import { loadAriaChatStyles } from "./styles/routeStyles";
import useAppEnvironment from "./hooks/useAppEnvironment";
import { ariaChatBrandClass } from "./utils/ariaChatBrandClass";
import { 
    initPushNotifications,
    registerPushTokenAfterLogin,
    initForegroundNotificationListener,
    initTokenRefreshListener,
    handleNotificationTap,
} from "./services/pushNotificationService";

/** Axios and similar clients attach HTTP status on `error.response.status`. */
function getHttpStatus(error: unknown): number | undefined {
    if (!error || typeof error !== "object") return undefined;
    const e = error as { response?: { status?: number }; status?: number };
    return e.response?.status ?? (typeof e.status === "number" ? e.status : undefined);
}
// Lazy load AriaChatStandalone to reduce initial bundle size
const AriaChatStandalone = lazy(() => import("./components/Standalone/AriaChatStandalone"));

/**
 * Build-time flag: REACT_APP_TARGET=portal means this build is for the
 * Aptive Index Portal native app (com.aptiveindex.mobile).
 * When unset or set to anything else, native builds show Aria Chat standalone.
 */
const isPortalTarget = process.env.REACT_APP_TARGET === 'portal';
const googleWebClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const googleIosClientId = isPortalTarget
    ? process.env.REACT_APP_GOOGLE_IOS_CLIENT_ID_PORTAL  || ''
    : process.env.REACT_APP_GOOGLE_IOS_CLIENT_ID_ARIA || '';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            // Retrying 429 amplifies rate-limit storms; allow limited retries for other failures.
            retry: (failureCount, error) => {
                if (getHttpStatus(error) === 429) return false;
                return failureCount < 1;
            },
            // Reduce automatic background refetch bursts across tabs/pages.
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
        },
        mutations: {
            retry: (failureCount, error) => {
                if (getHttpStatus(error) === 429) return false;
                return failureCount < 1;
            },
        },
    },
});

const ASK_ARIA_BRAND_CLASS = ariaChatBrandClass(true);

/** Native Ask Aria shell: body classes + CSS before lazy AriaChatStandalone hydrates. */
const NativeAskAriaBootstrap: React.FC = () => {
    useAppEnvironment(true);

    useEffect(() => {
        void loadAriaChatStyles();
    }, []);

    return null;
};

// Inner component that uses navigation (must be inside BrowserRouter)
const MobileAppContent: React.FC<{ darkMode: 'light' | 'dark' | 'system'; setDarkMode: (mode: 'light' | 'dark' | 'system') => void }> = ({ darkMode, setDarkMode }) => {
    return (
        <>
            <NativeAskAriaBootstrap />
            <Suspense fallback={
                <div className={`aria-standalone-loading ${ASK_ARIA_BRAND_CLASS}`}>
                    Loading Ask Aria...
                </div>
            }>
                <AriaChatStandalone 
                    darkMode={darkMode} 
                    setDarkMode={setDarkMode} 
                />
            </Suspense>
        </>
    );
};

/** Wires FCM tap handling for Ask Aria native (same pattern as MainApp PushNotificationHandler). */
const StandalonePushNotificationHandler: React.FC = () => {
    const navigate = useNavigate();
    useEffect(() => {
        const cleanup = handleNotificationTap(navigate);
        return cleanup;
    }, [navigate]);
    return null;
};

// Wrapper component for mobile app that connects DarkMode context to AriaChatStandalone
const MobileAppWrapper: React.FC = () => {
    const { darkMode, setDarkMode } = useDarkMode();
    
    return (
        <BrowserRouter>
            <StandalonePushNotificationHandler />
            <RouteUsageTracker />
            <MobileAppContent darkMode={darkMode} setDarkMode={setDarkMode} />
        </BrowserRouter>
    );
};

function App() {
    // CHANGED: Added this ref to prevent duplicate initialization
    const pushNotificationsInitialized = useRef(false);
    
    // Detect if running in Capacitor mobile app
    const isMobileApp = useMemo(() => {
        return Capacitor.isNativePlatform();
    }, []);

    // Pull-to-refresh safety net — iOS only, no-op everywhere else
    usePullToRefresh();

    // Apply native-app class immediately so startup CSS (full-viewport g100) applies before routes mount
    useEffect(() => {
        if (isMobileApp) {
            document.body.classList.add('is-native-app');
        }
    }, [isMobileApp]);

    const [user, setUser] = useState(() => {
        try {
            return loadUserFromStorage();
        } catch (error) {
            console.error("Error parsing stored user:", error);
            return null;
        }
    });

    useSessionRefresh({ enabled: Boolean(user) });

    const checkTokenExpiration = useCallback(() => {
        // Skip token expiration checks for mobile app - let AriaChatStandalone handle it
        if (isMobileApp) {
            return;
        }

        try {
            const storedUser = loadUserFromStorage();
            const userToken = storedUser?.token;
            if (!userToken) {
                setUser(null);
                return;
            }

            const decodedToken: any = jwtDecode(userToken) || "";
            const expirationTime = decodedToken?.exp * 1000;
            const currentTime = Date.now();
            const timeUntilExpiration = expirationTime - currentTime;

            if (timeUntilExpiration > 0) {
                setTimeout(() => {
                    setUser(null);
                    endSessionAndRedirect(LOGIN_REASONS.session_expired);
                }, timeUntilExpiration);
            } else {
                setUser(null);
                endSessionAndRedirect(LOGIN_REASONS.session_expired);
            }
        } catch (error) {
            console.error("Error checking token expiration:", error);
            localStorage.removeItem("user");
            setUser(null);
            endSessionAndRedirect(LOGIN_REASONS.session_expired);
        }
    }, [isMobileApp]);

    // Token expiration check - only run once on mount
    useEffect(() => {
        checkTokenExpiration();
    }, [checkTokenExpiration]);

        useEffect(() => {
            if (isMobileApp && !pushNotificationsInitialized.current) {
                pushNotificationsInitialized.current = true;

                // Initialize push notifications
                initPushNotifications().catch(console.error);
                initForegroundNotificationListener();
                initTokenRefreshListener();

                // Initialize Google Sign-In for native platforms
                SocialLogin.initialize({
                    google: {
                        webClientId: googleWebClientId,
                        iOSClientId: googleIosClientId,
                        mode: 'online',
                    },
                }).catch((err: any) => {
                    console.error('SocialLogin initialization error:', err);
                });
            }
        }, [isMobileApp]);

    // Effect 2: Register token after login
    useEffect(() => {
        if (isMobileApp && user) {
            registerPushTokenAfterLogin().catch(console.error);
        }
    }, [user, isMobileApp]); // Only depends on user

    // Listen for user state changes (e.g., after account creation/login without page reload)
    useEffect(() => {
        const handleUserStateChange = (event: CustomEvent) => {
            const newUser = event.detail;
            if (!newUser) {
                setUser(null);
                return;
            }
            // Re-parse through storage sanitizer to keep security-sensitive claims consistent.
            localStorage.setItem("user", JSON.stringify(newUser));
            setUser(loadUserFromStorage());
        };

        window.addEventListener('userStateChanged', handleUserStateChange as EventListener);
        
        return () => {
            window.removeEventListener('userStateChanged', handleUserStateChange as EventListener);
        };
    }, []);
    

    // If running in Capacitor mobile app as the Portal build, render the full portal
    if (isMobileApp && isPortalTarget) {
        return (
            <QueryClientProvider client={queryClient}>
                <BackgroundStreamProvider>
                <DarkModeProvider>
                    <ProfilePictureProvider>
                        {user ? (
                            <SuperAdminProvider>
                                <MainApp
                                    user={user}
                                    setUser={setUser}
                                />
                            </SuperAdminProvider>
                        ) : (
                            <MainApp
                                user={user}
                                setUser={setUser}
                            />
                        )}
                    </ProfilePictureProvider>
                </DarkModeProvider>
                </BackgroundStreamProvider>
            </QueryClientProvider>
        );
    }

    // If running in Capacitor mobile app as the Aria Chat build, only render Aria standalone
    if (isMobileApp) {
        return (
            <QueryClientProvider client={queryClient}>
                <BackgroundStreamProvider>
                <DarkModeProvider>
                    <ProfilePictureProvider>
                        <MobileAppWrapper />
                    </ProfilePictureProvider>
                </DarkModeProvider>
                </BackgroundStreamProvider>
            </QueryClientProvider>
        );
    }

    // Web app - render full MainApp with all routes
    return (
        <QueryClientProvider client={queryClient}>
            <BackgroundStreamProvider>
            <DarkModeProvider>
                <ProfilePictureProvider>
                    {user ? (
                        <SuperAdminProvider>
                            <MainApp
                                user={user}
                                setUser={setUser}
                            />
                        </SuperAdminProvider>
                    ) : (
                        <MainApp
                            user={user}
                            setUser={setUser}
                        />
                    )}
                </ProfilePictureProvider>
            </DarkModeProvider>
            </BackgroundStreamProvider>
        </QueryClientProvider>
    );
}

export default App;